/**
 * 语音录制纯状态机（任务 14.2b-2）：时钟注入可单测，DOM/MediaRecorder 接线在
 * useVoiceRecorder。交互口径（对齐旧版 chat_screen 录音手势）：
 * - 移动端长按录音：松开发送、左滑超过 CANCEL_SLIDE_PX 后松开取消；
 * - 桌面点击起止（无滑动取消，另给显式取消按钮）；
 * - 短于 MIN_VOICE_DURATION_MS 丢弃（提示「录音时间太短」，voiceSend.ts 单点）。
 *
 * starting 相位覆盖 getUserMedia 等待期：期间松手/取消先回 idle 记作中止，
 * 权限晚到时 mediaReady() 返回 'discard' 让调用方直接释放媒体流（权限弹窗
 * 停留多久都不会「松了手还在录」）。
 */
import { MIN_VOICE_DURATION_MS } from '~/chat/voiceSend';

/** 左滑取消阈值（旧版同口径 72px） */
export const CANCEL_SLIDE_PX = 72;

export type VoiceRecorderPhase = 'idle' | 'starting' | 'recording';

export type VoiceStopResult =
  | { kind: 'send'; durationMs: number }
  | { kind: 'cancel' }
  | { kind: 'too-short' };

export interface VoiceRecorderMachine {
  readonly phase: VoiceRecorderPhase;
  /** recording 相位的已录时长；其余相位恒 0 */
  elapsedMs(): number;
  /** 移动端左滑已越过取消阈值（松开即取消） */
  cancelArmed(): boolean;
  /** 开始：仅 idle 受理（返回 'request-media' 让调用方去 getUserMedia） */
  start(): 'request-media' | 'ignore';
  /** getUserMedia 成功：未被中止则进入 recording，否则让调用方释放流 */
  mediaReady(): 'record' | 'discard';
  /** getUserMedia 失败：回 idle（错误文案由调用方按 recorderErrorText 提示） */
  mediaFailed(): void;
  /** 移动端按住期间的水平位移（相对按下点，<0 为左滑） */
  slide(dx: number): void;
  /** 松手/点击停止：按滑动态与时长裁决发送、取消或太短 */
  stop(): VoiceStopResult;
  /** 显式取消（桌面取消按钮 / pointercancel / 卸载清理），任意相位安全 */
  cancel(): void;
}

export function createVoiceRecorderMachine(now: () => number): VoiceRecorderMachine {
  let phase: VoiceRecorderPhase = 'idle';
  let startedAt = 0;
  let slideDx = 0;

  return {
    get phase() {
      return phase;
    },
    elapsedMs() {
      return phase === 'recording' ? now() - startedAt : 0;
    },
    cancelArmed() {
      return phase === 'recording' && slideDx <= -CANCEL_SLIDE_PX;
    },
    start() {
      if (phase !== 'idle') return 'ignore';
      phase = 'starting';
      slideDx = 0;
      return 'request-media';
    },
    mediaReady() {
      if (phase !== 'starting') return 'discard';
      phase = 'recording';
      startedAt = now();
      return 'record';
    },
    mediaFailed() {
      if (phase === 'starting') phase = 'idle';
    },
    slide(dx) {
      if (phase === 'recording') slideDx = dx;
    },
    stop() {
      if (phase === 'starting') {
        // 权限还没下来就松手：什么都没录上，按太短口径提示
        phase = 'idle';
        return { kind: 'too-short' };
      }
      if (phase !== 'recording') return { kind: 'cancel' };
      const armed = this.cancelArmed();
      const durationMs = now() - startedAt;
      phase = 'idle';
      if (armed) return { kind: 'cancel' };
      if (durationMs < MIN_VOICE_DURATION_MS) return { kind: 'too-short' };
      return { kind: 'send', durationMs };
    },
    cancel() {
      phase = 'idle';
    },
  };
}

/** getUserMedia 失败 → 用户提示（旧版同口径：权限拒绝与设备不支持分开说） */
export function recorderErrorText(err: unknown): string {
  const name =
    typeof err === 'object' && err !== null && 'name' in err ? String(err.name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return '请开启麦克风权限';
  }
  return '当前设备不支持语音录制';
}

/** 语音发送结果 → 提示文案（'ok' 空串；口径同 imageSendErrorText） */
export function voiceSendErrorText(outcome: string): string {
  switch (outcome) {
    case 'ok':
      return '';
    case 'too-large':
      return '语音过大，无法发送';
    default:
      return '语音发送失败，请重试';
  }
}
